import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { Item, Owner, Review } from '../types';
import { formatGBP } from '../utils/currency';
import StarRating from './StarRating';
import './OwnerPage.css';

// Use the same mock owners data
const mockOwners: Record<string, Owner> = {
  'john-smith': { id: 'john-smith', name: 'John Smith', rating: 4.5, totalRatings: 24 },
  'sarah-johnson': { id: 'sarah-johnson', name: 'Sarah Johnson', rating: 5, totalRatings: 31 },
  'mike-wilson': { id: 'mike-wilson', name: 'Mike Wilson', rating: 4, totalRatings: 15 },
  'emma-davis': { id: 'emma-davis', name: 'Emma Davis', rating: 4.8, totalRatings: 42 },
  'lisa-brown': { id: 'lisa-brown', name: 'Lisa Brown', rating: 4.2, totalRatings: 18 },
  'david-clark': { id: 'david-clark', name: 'David Clark', rating: 4.7, totalRatings: 28 },
  'amy-white': { id: 'amy-white', name: 'Amy White', rating: 4.9, totalRatings: 35 }
};

// Mock reviews data
const mockReviews: Record<string, Review[]> = {
  'john-smith': [
    {
      id: '1',
      rating: 5,
      comment: "Great tools, always well-maintained!",
      reviewerName: "Alice Brown",
      reviewerId: "alice-1",
      date: "2024-02-15",
      itemId: "lawn-mower-1",
      itemName: "Professional Lawn Mower"
    }
  ],
  'sarah-johnson': [
    {
      id: '2',
      rating: 4,
      comment: "Very reliable and friendly owner",
      reviewerName: "Bob Wilson",
      reviewerId: "bob-1",
      date: "2024-02-10",
      itemId: "hedge-trimmer-1",
      itemName: "Cordless Hedge Trimmer"
    }
  ]
};

const getOwner = (ownerId: string): Owner => {
  return mockOwners[ownerId] || { id: ownerId, name: 'Unknown Owner', rating: 0, totalRatings: 0 };
};

const OwnerPage: React.FC = () => {
  const { ownerId } = useParams<{ ownerId: string }>();
  const { categories, loading, getOwner } = useData();
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [owner, setOwner] = useState<Owner | null>(null);

  // Find owner's items across all categories
  const ownerItems = categories.flatMap(category =>
    category.items.filter(item => item.ownerId === ownerId)
  );

  const reviews = ownerId ? (mockReviews[ownerId] || []) : [];
  const selectedItemData = ownerItems.find(item => item.id === selectedItem);

  // Load owner data when ownerId changes
  useEffect(() => {
    const loadOwner = async () => {
      if (ownerId) {
        const ownerData = await getOwner(ownerId);
        if (ownerData) {
          setOwner(ownerData);
        } else {
          // Fallback to mock owner
          setOwner(mockOwners[ownerId] || { id: ownerId, name: 'Unknown Owner', rating: 0, totalRatings: 0 });
        }
      }
    };

    loadOwner();
  }, [ownerId, getOwner]);

  if (loading) {
    return (
      <div className="owner-page">
        <div style={{ padding: '20px', textAlign: 'center' }}>
          Loading owner information...
        </div>
      </div>
    );
  }

  if (!owner) {
    return (
      <div className="owner-page">
        <div style={{ padding: '20px', textAlign: 'center' }}>
          Owner not found
        </div>
      </div>
    );
  }

  return (
    <div className="owner-page">
      <div className="owner-header">
        <h2>{owner.name}'s Items</h2>
        <div className="owner-rating">
          <StarRating rating={owner.rating} totalRatings={owner.totalRatings} />
        </div>
      </div>
      
      <div className="owner-content">
        <div className="owner-main">
          <div className="items-grid">
            {ownerItems.map((item: Item) => (
              <div key={item.id} className="item-card" onClick={() => setSelectedItem(item.id)}>
                <img 
                  src={item.images[0] || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4='} 
                  alt={item.name}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    const target = e.currentTarget;
                    target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
                  }}
                  className="item-image"
                />
                <div className="item-info">
                  <h3>{item.name}</h3>
                  <div className="item-pricing">
                    <p className="daily-rate">£{item.dailyRateGBP.toFixed(2)}/day</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="reviews-section">
          <h3>Reviews</h3>
          {reviews.map((review: Review) => (
            <div key={review.id} className="review">
              <div className="review-header">
                <StarRating rating={review.rating} size="small" totalRatings={0} showCount={false} />
                <span className="review-date">{review.date}</span>
              </div>
              <p className="review-comment">{review.comment}</p>
              <p className="reviewer-name">- {review.reviewerName}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OwnerPage; 